import { PlayableRunStateV2Schema } from '@dissidium/contracts';
import { canonical, stripStoredLedger } from '@dissidium/sim';
import { SaveCorruptError } from '../errors.ts';

export class SaveVersionError extends SaveCorruptError {
  constructor(version: unknown) {
    super(`This life uses save version ${String(version)}, which this build cannot read. Its save has been kept. Open it with a compatible version of Dissidium.`);
    this.name = 'SaveVersionError';
  }
}

export type SaveMigration = (state: Record<string, unknown>) => Record<string, unknown>;

/** v2→v3: every tactical ledger must equal the life ledger before it is stripped.
 * A mismatch is a corrupt save: it goes to the existing shell quarantine and the
 * original bytes are kept, never overwritten and never silently dropped. */
export const migrateLedgerDedupe: SaveMigration = input => {
  const v2 = PlayableRunStateV2Schema.parse(input);
  for (const scene of Object.values(v2.world.scenes)) {
    const tactics = scene.combat.tactics;
    if (tactics && (tactics.continuity === undefined || canonical(tactics.continuity) !== canonical(v2.continuity))) {
      throw new SaveCorruptError('Tactical combat and the playable life disagree about the continuity ledger; the original save has been kept');
    }
  }
  return { ...stripStoredLedger(v2), version: 3 };
};

/** Upgrade the recovered whole state, never a checkpoint while its older journal remains. */
export function migratePlayableSave<T>(input: unknown, targetVersion: number, migrations: Readonly<Record<number, SaveMigration>>, validate: (state: unknown) => T): T {
  if (!input || typeof input !== 'object' || !('format' in input) || input.format !== 'playable-run') throw new SaveCorruptError('This is not a playable life. Laboratory saves cannot be imported into this chronology.');
  let state = structuredClone(input) as Record<string, unknown>;
  const original = structuredClone(state);
  if (!Number.isSafeInteger(state.version) || Number(state.version) < 1 || Number(state.version) > targetVersion) throw new SaveVersionError(state.version);
  while (Number(state.version) < targetVersion) {
    const version = Number(state.version);
    const migrate = migrations[version];
    if (!migrate) throw new SaveVersionError(version);
    state = migrate(structuredClone(state));
    if (state.version !== version + 1 || state.format !== 'playable-run') throw new SaveCorruptError('A migration must advance exactly one playable save version');
  }
  // Migrations change representation, never the life that was actually lived.
  for (const key of ['envelope', 'frozenContinuity', 'continuity', 'pcId']) {
    if (canonical(state[key]) !== canonical(original[key])) throw new SaveCorruptError(`A save migration changed ${key}; the original save has been kept`);
  }
  return validate(state);
}
